import { EngagementOptions } from '@/constants/engagements';
import { ServiceOptions } from '@/constants/services';

export const Hero = {
  title: 'Marketing for facade installers and manufacturers',
  description: 'We help glazing contractors, cladding installers, and facade manufacturers win more work, with marketing built around the building envelope industry.',
  secondaryLink: { label: 'See how it works', to: '#process' },
};

export const Services = {
  title: 'Marketing built for the building envelope',
  description: 'Every service is shaped around how installers win bids and how manufacturers get specified.',
  items: ServiceOptions,
};

export const Process = {
  title: 'How we work',
  description: 'Four steps from a first call to marketing that brings in work.',
  steps: [
    { title: 'Discovery call', description: 'We learn your services, your best projects, and the clients you want more of.' },
    { title: 'Marketing plan', description: 'You get a plan with channels, content, and targets before any work starts.' },
    { title: 'Launch', description: 'We build the pieces and launch the campaigns, with your approval at each step.' },
    { title: 'Measure and refine', description: "Monthly reports show what's working, and we shift effort toward it." },
  ],
};

export const Clients = {
  title: 'Who we work with',
  description: 'Companies that make and install the building envelope.',
  items: [
    { title: 'Glazing contractors', description: 'Curtain wall, storefront, and window installers who want more bid invites from the right general contractors.' },
    { title: 'Cladding and panel installers', description: 'Metal panel, rainscreen, and composite installers building a reputation beyond word of mouth.' },
    { title: 'System manufacturers', description: 'Curtain wall, window, and door manufacturers who want architects to specify their systems.' },
    { title: 'Material manufacturers', description: 'Glass, panel, and cladding manufacturers growing their dealer and installer networks.' },
  ],
};

export const Deliverables = {
  title: 'What you get every month',
  description: 'A clear monthly rhythm, so you always know what is being done and what it produced.',
  note: 'Everything we create is yours to keep, including photos, copy, and ad accounts.',
  packageTitle: 'A typical month',
  contents: [
    { title: 'Planning call', detail: 'Priorities, projects, and targets for the month' },
    { title: 'Content production', detail: 'Project posts, case studies, and photos' },
    { title: 'Campaigns and outreach', detail: 'Email, LinkedIn, and ad campaigns go live' },
    { title: 'Lead follow-up', detail: 'Replies and inquiries routed to your team' },
    { title: 'Performance report', detail: 'Leads, meetings, and bid invites in plain numbers' },
    { title: 'Review call', detail: 'What worked, and what changes next month' },
  ],
};

export const Faq = {
  title: 'Questions companies ask us',
  description: 'The short answers. Contact us for anything specific to your company.',
  items: [
    {
      question: 'Do you only work with facade companies?',
      answer: 'Yes. We work only with facade installers and manufacturers, so we already know your clients, your projects, and your sales cycle.',
    },
    {
      question: 'Do we need a new website to get started?',
      answer: 'Not always. We review your current site first and only recommend a rebuild if it is holding your marketing back.',
    },
    {
      question: 'How soon will we see results?',
      answer: 'It depends on the channel. Outreach and ads can start conversations within weeks, while search and specification work builds over months. Your plan sets expectations for each.',
    },
    {
      question: 'How is pricing set?',
      answer: 'Pricing depends on the channels and the amount of work each month. You approve the price before anything starts.',
    },
    {
      question: 'Will you need photos and details of our projects?',
      answer: 'Yes, and we make it easy. We can coordinate photo shoots of finished projects and interview your team for case studies.',
    },
    {
      question: 'Can we change the plan as our workload changes?',
      answer: 'Yes. Plans can shift channels or pause when your backlog is full, and ramp back up when you need more work.',
    },
  ],
};

export const Engagement = {
  title: 'Ways to work with us',
  description: 'Start with a single project, or bring us on as your marketing team.',
  bestForLabel: 'Best for:',
  options: EngagementOptions,
};

export const HomeCta = {
  title: 'Ready to win more facade work?',
  description: "Tell us about your company, and we'll reply with a marketing plan and a price.",
};